import axiosInstance from './axiosInstance';
import config from '../config/config';

const FALLBACK_CONFIG = { auth: { tokenKey: 'authToken', refreshTokenKey: 'refreshToken', userKey: 'user' } };
const currentConfig = config || FALLBACK_CONFIG;

export interface LoginCredentials {
  phone: string;
  password: string;
}

export interface AuthResponse {
  success: boolean;
  accessToken: string;
  refreshToken: string;
  user: { id: number; name: string };
}

export interface User {
  id: number;
  name: string;
}

export interface AuthStatusResponse {
  success: boolean;
  isAuthenticated: boolean;
  userName: string;
  userId: string;
}

class AuthApi {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await axiosInstance.post<AuthResponse>('/auth/login', credentials);

      if (response.data.success) {
        localStorage.setItem(currentConfig.auth.tokenKey, response.data.accessToken);
        localStorage.setItem(currentConfig.auth.refreshTokenKey, response.data.refreshToken);
        localStorage.setItem(currentConfig.auth.userKey, JSON.stringify(response.data.user));
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
        return response.data;
      }

      throw new Error((response.data as any).error || 'Ошибка входа');
    } catch (error: any) {
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        const apiUrl = axiosInstance.defaults.baseURL || 'неизвестен';
        throw new Error(`Не удалось подключиться к серверу.\nAPI URL: ${apiUrl}`);
      }
      if (error.response) {
        throw new Error(error.response.data?.error || error.response.data?.message || 'Неизвестная ошибка сервера');
      }
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem(currentConfig.auth.refreshTokenKey);
      if (refreshToken) {
        await axiosInstance.post('/auth/logout', { refreshToken }).catch(() => {});
      }
    } finally {
      this._clearStorage();
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const token = localStorage.getItem(currentConfig.auth.tokenKey);
      if (!token) return null;

      const response = await axiosInstance.get<AuthStatusResponse>('/auth/status');
      if (response.data.success && response.data.isAuthenticated) {
        return { id: parseInt(response.data.userId), name: response.data.userName };
      }
      return null;
    } catch {
      return null;
    }
  }

  async refreshToken(): Promise<string | null> {
    try {
      const refreshToken = localStorage.getItem(currentConfig.auth.refreshTokenKey);
      if (!refreshToken) return null;

      const response = await axiosInstance.post<AuthResponse>('/auth/refresh', { refreshToken });

      if (response.data.success) {
        localStorage.setItem(currentConfig.auth.tokenKey, response.data.accessToken);
        localStorage.setItem(currentConfig.auth.refreshTokenKey, response.data.refreshToken);
        localStorage.setItem(currentConfig.auth.userKey, JSON.stringify(response.data.user));
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
        return response.data.accessToken;
      }

      return null;
    } catch {
      this._clearStorage();
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(currentConfig.auth.tokenKey);
  }

  getStoredUser(): User | null {
    const userStr = localStorage.getItem(currentConfig.auth.userKey);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  _clearStorage(): void {
    localStorage.removeItem(currentConfig.auth.tokenKey);
    localStorage.removeItem(currentConfig.auth.refreshTokenKey);
    localStorage.removeItem(currentConfig.auth.userKey);
    delete axiosInstance.defaults.headers.common['Authorization'];
  }
}

export const authApi = new AuthApi();
